import React from 'react';
import {observer} from 'mobx-react-lite';
import classNames from 'classnames';
import {useAppState, useMobileNavController, useRootStore} from "@stores/AppStateContext";

import {PageName} from "@stores/ViewStore";

import {RootStoreModel} from "@stores/RootStore";

import {screenSizeProvider} from "@stores/ScreenSizeProvider";
import {WorkspaceSelect} from "../../components/WorkspaceSelect/WorkspaceSelect";
import {WorkspaceAvatar} from "../../components/Avatar/Avatar";
import {LeftPanelClose} from "../../components/icons/LeftPanelClose";
import {LeftPanelOpen} from "../../components/icons/LeftPanelOpen";
import {Tooltip} from "../../components/Tooltip";
import {UpgradePlanCard} from "./UpgradePlanCard";
import {isFreeTrial} from "@stores/client";

import {NAVIGATION_ITEMS, NavigationItem, SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_WIDTH_EXPANDED} from './navigationConfig';


import './MainSidebar.css';


export const MainSidebar = observer(() => {
    const appState = useAppState();
    const rootStore = useRootStore();
    const mobileNav = useMobileNavController();
    const isCollapsed = rootStore.isSidebarCollapsed;

    const activePage = rootStore.view.page as PageName;
    const isMobile = screenSizeProvider.view !== 'desktop';

    const handleNav = (page: PageName | 'help-external') => () => {
        if (page === 'help-external') {
            window.open('/help', '_blank');
            return;
        }
        rootStore.view.openPageByName(page);
        if (isMobile) {
            // reaction doesn't work if page is not changed.
            mobileNav.ensureLayerForCurrentContext(page);
        }
    };

    const tools = NAVIGATION_ITEMS.filter(i => i.section === 'tools');
    const settings = NAVIGATION_ITEMS.filter(i => i.section === 'settings');


    // Mobile overlay visibility
    if (isMobile && !mobileNav.showMainSidebar) return null;

    const renderMenuSection = (menu: NavigationItem[]) => {
        return menu.map(item => {
            const Icon = item.icon;
            const isActive = item.subPages.includes(activePage);
            return (
                <div
                    key={item.id}
                    className={classNames('MainSidebar_item', {
                        'MainSidebar_item--active': isActive,
                    })}
                    onClick={() => {
                        handleNav(item.page)();
                    }}
                >
                    <Icon size={24}/>
                    {!isCollapsed && <span className="MainSidebar__label">{item.label}</span>}
                </div>
            );
        })
    }

    return (
        <aside className={classNames('MainSidebar', 'theme-dark', {
            'MainSidebar--collapsed': isCollapsed,
            'MainSidebar--overlay': isMobile,
        })}
        >
            {!isMobile && (<MainSidebarHeader/>)}
            <div className="MainSidebar__inner title-small">
                <nav className="MainSidebar_section">
                    {renderMenuSection(tools)}
                </nav>
                <nav className="MainSidebar_section">
                    {rootStore.isReady && isFreeTrial(appState.userState!.clientDB!) && (<UpgradePlanCard/>)}
                    {renderMenuSection(settings)}
                </nav>
            </div>
        </aside>
    );
});

export const MainSidebarHeader = observer(() => {
    const appState = useAppState();
    const rootStore = useRootStore();
    const isCollapsed = rootStore.isSidebarCollapsed;

    return (
        <div className="MainSidebar__workspace">
            {!isCollapsed ? (
                <>
                    <WorkspaceSelect/>
                    <Tooltip title={'Collapse sidebar'}>
                        <LeftPanelClose
                            className="MainSidebar__collapseToggle"
                            aria-label={'Collapse sidebar'}
                            onClick={() => rootStore.toggleIsSidebarCollapsed(true)}
                            additionalPadding={2}
                        />
                    </Tooltip>
                </>
            ) : (
                <button
                    type="button"
                    className="MainSidebar__wsCollapsed"
                    onClick={() => rootStore.toggleIsSidebarCollapsed(false)}
                    aria-label={'Expand sidebar'}
                >
                    {appState.currentWorkspaceDb && (
                        <WorkspaceAvatar
                            workspace={appState.currentWorkspaceDb!}
                            size={32}
                            className={'MainSidebar__wsAvatar'}
                        />
                    )}
                    <LeftPanelOpen size={24} additionalPadding={4} className={'MainSidebar__wsOpenIcon'}/>
                </button>
            )}
        </div>
    )
})

export const getSidebarCurrentWidth = (rootStore: RootStoreModel) => {
    return rootStore.isSidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
}
